import { createSelector, createSlice } from '@reduxjs/toolkit';

import { SliceNameEnum } from '@app/enums';
import { defaultMapData, MapSearchState } from '@app/models';
import { ReservationTypeEnum } from '@app/models/reservation-type.model';
import { fetchMapAccommodations } from '@app/state/api-thunks/map-accommodations.thunk';
import {
    setIsMapLoadedAction,
    setLocationDetailsAction,
    setSelectedAccommodationAction,
} from '@app/state/slices/map-state.actions';

export const initialState: MapSearchState = {
    isMapLoaded: false,
    mapData: defaultMapData,
};

export const mapStateSlice = createSlice({
    name: SliceNameEnum.Map,
    initialState,
    reducers: {},
    extraReducers: builder => {
        builder.addCase(fetchMapAccommodations.pending, state => {
            state.mapData.isLoadingAccommodations = true;
            state.mapData.accommodationsError = null;
        });

        builder.addCase(fetchMapAccommodations.fulfilled, (state, { payload }) => {
            state.mapData.isLoadingAccommodations = false;
            state.mapData.accommodations = payload.accommodations;
            state.mapData.realtimeBookingModelAccommodationsCount = payload.accommodations.filter(
                acc => {
                    return acc.reservationType === ReservationTypeEnum.RealTime;
                },
            ).length;
            state.mapData.accommodationsError = null;
        });

        builder.addCase(fetchMapAccommodations.rejected, (state, { payload }) => {
            state.mapData.isLoadingAccommodations = false;
            state.mapData.accommodationsError =
                typeof payload === 'string' ? payload : 'Failed to fetch map accommodations';
        });

        builder.addCase(setLocationDetailsAction, (state, { payload }) => {
            state.mapData.locationDetails = payload.locationDetails;
        });

        builder.addCase(setSelectedAccommodationAction, (state, { payload }) => {
            state.mapData.selectedAccommodation = payload.accommodation;
        });

        builder.addCase(setIsMapLoadedAction, (state, { payload }) => {
            state.isMapLoaded = payload.isMapLoaded;
        });
    },
});

const selectSelfState = (state: { [SliceNameEnum.Map]: MapSearchState }): MapSearchState => {
    return state[mapStateSlice.name] ?? initialState;
};

export const selectors = {
    selectMapAccommodations: createSelector(selectSelfState, state => state.mapData.accommodations),
    selectMapRealtimeBookingModelAccommodationsCount: createSelector(
        selectSelfState,
        state => state.mapData.realtimeBookingModelAccommodationsCount,
    ),

    selectLocationDetails: createSelector(selectSelfState, state => state.mapData.locationDetails),

    selectIsLoadingAccommodations: createSelector(
        selectSelfState,
        state => state.mapData.isLoadingAccommodations,
    ),

    selectIsMapLoaded: createSelector(selectSelfState, state => state.isMapLoaded),

    selectAccommodationsError: createSelector(
        selectSelfState,
        state => state.mapData.accommodationsError,
    ),

    selectSelectedAccommodation: createSelector(
        selectSelfState,
        state => state.mapData.selectedAccommodation,
    ),
};

export default mapStateSlice.reducer;
